import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js";
import Alert from '../common/Alert.js';
import loanReceivedService from '../../services/LoanReceivedService.js';
import {
  CheckCircle, ArrowLeft, Pencil, Trash2, HandCoins, XCircle,
  CreditCard, Building2, Calendar, TrendingDown, AlertTriangle,
  FileText, Zap, Plus, ChevronRight, ExternalLink, Hash,
  Phone, Mail, MapPin, Landmark, ReceiptText, User
} from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const ViewLoanReceived = () => {
  const { t } = useTranslation();
  const { loanId } = useParams();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const navigate = useNavigate();

  useEffect(() => { fetchLoan(); }, [loanId]);

  const fetchLoan = async () => {
    try {
      setLoading(true);
      const response = await loanReceivedService.getById(loanId);
      setLoan(response.data);
    } catch (err) {
      Alert.error('Failed to load loan data');
      console.error(err);
      navigate('/loans-received');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (window.confirm('Are you sure you want to approve this loan?')) {
      try {
        await loanReceivedService.approve(loanId);
        Alert.success('Loan approved successfully');
        fetchLoan();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to approve loan');
      }
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) { Alert.error('Please provide a rejection reason'); return; }
    try {
      await loanReceivedService.reject(loanId, rejectionReason);
      Alert.success('Loan rejected successfully');
      setShowRejectModal(false);
      setRejectionReason('');
      fetchLoan();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to reject loan');
    }
  };

  const handleActivate = async () => {
    if (window.confirm('Are you sure you want to activate this loan?')) {
      try {
        await loanReceivedService.activate(loanId);
        Alert.success('Loan activated successfully');
        fetchLoan();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to activate loan');
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this loan? This action cannot be undone.')) {
      try {
        await loanReceivedService.delete(loanId);
        Alert.success('Loan deleted successfully');
        navigate('/loans-received');
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete loan');
      }
    }
  };

  const viewAgreement = () => {
    if (loan?.agreement_url) window.open(loan.agreement_url, '_blank');
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusConfig = (status) => {
    const map = {
      pending:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-300',   dot: 'bg-amber-400',   label: 'Pending'   },
      approved:  { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-300',    dot: 'bg-blue-500',    label: 'Approved'  },
      active:    { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300', dot: 'bg-emerald-500', label: 'Active'    },
      completed: { bg: 'bg-gray-50',    text: 'text-gray-600',    border: 'border-gray-300',    dot: 'bg-gray-400',    label: 'Completed' },
      rejected:  { bg: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-300',     dot: 'bg-red-500',     label: 'Rejected'  },
      defaulted: { bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-300',  dot: 'bg-purple-500',  label: 'Defaulted' },
      cancelled: { bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-300',  dot: 'bg-orange-400',  label: 'Cancelled' }
    };
    return map[status] || map.pending;
  };

  const repaidPercent = loan
    ? Math.min(100, Math.round(((loan.amount_paid || 0) / (loan.loan_amount || 1)) * 100))
    : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading loan data..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!loan) return null;

  const statusCfg = getStatusConfig(loan.status);
  const tabs = [
    { key: 'details',    label: 'Loan Details'  },
    { key: 'lender',     label: 'Lender Info'   },
    { key: 'repayments', label: `Repayments (${loan.repayments_count || 0})` },
  ];

  return (
    <DashboardLayout>
              <div className="max-w-6xl mx-auto">

                {/* ── Page Header ── */}
                <div className="mb-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h1 className="text-xl sm:text-2xl font-bold text-secondary flex items-center gap-2">
                        <CreditCard className="w-6 h-6 text-buttonBg" />
                        Loan Details
                      </h1>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2">
                      {loan.status === 'pending' && (
                        <>
                          <Button variant="primary" icon={CheckCircle} onClick={handleApprove}>
                            Approve
                          </Button>
                          <Button variant="primary" icon={XCircle} onClick={() => setShowRejectModal(true)}>
                            Reject
                          </Button>
                          <Button variant="secondary" icon={Pencil} onClick={() => navigate(`/loans-received/edit/${loanId}`)}>
                            Edit
                          </Button>
                          <Button variant="primary" icon={Trash2} onClick={handleDelete}>
                            Delete
                          </Button>
                        </>
                      )}
                      {loan.status === 'approved' && (
                        <>
                          <Button variant="primary" icon={Zap} onClick={handleActivate}>
                            Activate
                          </Button>
                          <Button variant="primary" icon={Plus} onClick={() => navigate(`/loans-received/repayment/${loanId}`)}>
                            Add Repayment
                          </Button>
                        </>
                      )}
                      {loan.status === 'active' && (
                        <Button variant="primary" icon={Plus} onClick={() => navigate(`/loans-received/repayment/${loanId}`)}>
                          Add Repayment
                        </Button>
                      )}
                      {(loan.has_agreement || loan.agreement_url) && (
                        <Button variant="primary" icon={FileText} onClick={viewAgreement}>
                          Agreement
                        </Button>
                      )}
                      <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/loans-received')}>
                        Back
                      </Button>
                    </div>
                  </div>
                </div>

                {/* ── Hero Card ── */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-5">
                  <div className="bg-primary px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg font-bold text-secondary">{loan.loan_id}</span>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full border ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`}></span>
                          {statusCfg.label}
                        </span>
                        {loan.is_overdue && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700 border border-red-200">
                            <AlertTriangle className="w-3 h-3" /> Overdue
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-secondary font-medium">{loan.lender_name} · <span className="capitalize">{loan.lender_type?.replace(/_/g, ' ')}</span></p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-secondary/70 mb-0.5">Loan Amount</p>
                      <p className="text-2xl font-bold text-secondary">{formatCurrency(loan.loan_amount)}</p>
                    </div>
                  </div>

                  {/* Financial Summary Row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100">
                    {[
                      { label: 'Total Amount',  value: formatCurrency(loan.total_amount),  color: 'text-gray-800'    },
                      { label: 'Amount Paid',   value: formatCurrency(loan.amount_paid),   color: 'text-emerald-600' },
                      { label: 'Pending',       value: formatCurrency(loan.amount_pending),color: 'text-red-600'     },
                      { label: 'Interest Rate', value: `${loan.interest_rate}% p.a.`,      color: 'text-blue-600'    },
                    ].map((item, i) => (
                      <div key={i} className="p-4 text-center">
                        <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                        <p className={`text-base font-bold ${item.color}`}>{item.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Repayment Progress Bar */}
                  <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
                    <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                      <span>Repayment Progress</span>
                      <span className="font-semibold text-gray-700">{repaidPercent}% repaid</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${repaidPercent === 100 ? 'bg-emerald-500' : 'bg-orange-500'}`}
                        style={{ width: `${repaidPercent}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* ── Tabs ── */}
                <div className="bg-white rounded-xl shadow-sm mb-1 border-b border-gray-200">
                  <div className="flex overflow-x-auto">
                    {tabs.map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-5 sm:px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                          activeTab === tab.key
                            ? 'border-b-2 border-buttonBg text-buttonBg'
                            : 'text-gray-500 hover:text-gray-800'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Tab Content ── */}
                <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">

                  {/* ─ Details Tab ─ */}
                  {activeTab === 'details' && (
                    <div className="space-y-7">

                      {/* Loan Information */}
                      <section>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">
                          Loan Information
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                          {[
                            { label: 'Loan Type',         value: loan.loan_type?.replace(/_/g, ' ')    },
                            { label: 'Loan Amount',        value: formatCurrency(loan.loan_amount)       },
                            { label: 'Processing Fee',     value: formatCurrency(loan.processing_fee)    },
                            { label: 'Loan Account No.',   value: loan.loan_account_number || '—'        },
                            { label: 'Loan Date',          value: formatDate(loan.loan_date)             },
                            { label: 'Due Date',           value: formatDate(loan.due_date)              },
                            { label: 'Disbursed Date',     value: formatDate(loan.disbursed_date)        },
                          ].map((f, i) => (
                            <div key={i}>
                              <p className="text-xs text-gray-400 mb-1">{f.label}</p>
                              <p className="text-sm font-semibold text-gray-800 break-words capitalize">{f.value}</p>
                            </div>
                          ))}
                        </div>
                      </section>

                      {/* Repayment Details */}
                      <section>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">
                          Repayment Schedule
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                          {[
                            { label: 'Frequency',          value: loan.repayment_frequency?.replace(/_/g, ' ') },
                            { label: 'Total Installments', value: loan.total_installments                       },
                            { label: 'Installment Amount', value: formatCurrency(loan.installment_amount)       },
                          ].map((f, i) => (
                            <div key={i}>
                              <p className="text-xs text-gray-400 mb-1">{f.label}</p>
                              <p className="text-sm font-semibold text-gray-800 capitalize">{f.value}</p>
                            </div>
                          ))}
                        </div>
                      </section>

                      {/* Additional Info */}
                      <section>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">
                          Additional Information
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Purpose</p>
                            <p className="text-sm text-gray-800 bg-gray-50 p-3 rounded-xl leading-relaxed">{loan.purpose}</p>
                          </div>
                          {loan.collateral_details && (
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Collateral Details</p>
                              <p className="text-sm text-gray-800 bg-gray-50 p-3 rounded-xl leading-relaxed">{loan.collateral_details}</p>
                            </div>
                          )}
                          {loan.remarks && (
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Remarks</p>
                              <p className="text-sm text-gray-800 bg-gray-50 p-3 rounded-xl leading-relaxed">{loan.remarks}</p>
                            </div>
                          )}
                        </div>
                      </section>

                      {/* Rejection Banner */}
                      {loan.status === 'rejected' && loan.rejection_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
                          <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-red-800 mb-1">Rejection Reason</p>
                            <p className="text-sm text-red-700">{loan.rejection_reason}</p>
                          </div>
                        </div>
                      )}

                      {/* Audit Information */}
                      <section>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">
                          Audit Information
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          {[
                            { label: 'Created By',   value: loan.created_by                     },
                            { label: 'Created At',   value: formatDateTime(loan.created_at)      },
                            { label: 'Last Updated', value: formatDateTime(loan.updated_at)      },
                            ...(loan.approved_by ? [
                              { label: 'Approved By',   value: loan.approved_by               },
                              { label: 'Approved Date', value: formatDateTime(loan.approved_date) },
                            ] : [])
                          ].map((f, i) => (
                            <div key={i}>
                              <p className="text-xs text-gray-400 mb-1">{f.label}</p>
                              <p className="text-sm font-semibold text-gray-800 break-words">{f.value}</p>
                            </div>
                          ))}
                        </div>
                      </section>
                    </div>
                  )}

                  {/* ─ Lender Tab ─ */}
                  {activeTab === 'lender' && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">
                        Lender Details
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-5">
                        {[
                          { label: 'Lender Type',    value: loan.lender_type?.replace(/_/g, ' '), icon: <Building2 className="w-4 h-4 text-gray-400" />  },
                          { label: 'Lender Name',    value: loan.lender_name,                      icon: <Landmark className="w-4 h-4 text-gray-400" />   },
                          { label: 'Contact Number', value: loan.lender_contact,                   icon: <Phone className="w-4 h-4 text-gray-400" />      },
                          { label: 'Email',          value: loan.lender_email || '—',              icon: <Mail className="w-4 h-4 text-gray-400" />       },
                          { label: 'Account Number', value: loan.account_number || '—',            icon: <Hash className="w-4 h-4 text-gray-400" />       },
                          { label: 'IFSC Code',      value: loan.ifsc_code || '—',                 icon: <Hash className="w-4 h-4 text-gray-400" />       },
                          ...(loan.lender_address ? [{ label: 'Address', value: loan.lender_address, icon: <MapPin className="w-4 h-4 text-gray-400" />, span: true }] : [])
                        ].map((f, i) => (
                          <div key={i} className={f.span ? 'sm:col-span-2' : ''}>
                            <div className="flex items-center gap-1.5 mb-1">
                              {f.icon}
                              <p className="text-xs text-gray-400">{f.label}</p>
                            </div>
                            <p className="text-sm font-semibold text-gray-800 capitalize break-words">{f.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ─ Repayments Tab ─ */}
                  {activeTab === 'repayments' && (
                    <div>
                      <div className="flex items-center justify-between mb-5">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Repayment History</h3>
                          <p className="text-xs text-gray-400 mt-0.5">{loan.repayments?.length || 0} payment{loan.repayments?.length !== 1 ? 's' : ''} recorded</p>
                        </div>
                        {(loan.status === 'approved' || loan.status === 'active') && (
                          <button
                            onClick={() => navigate(`/loans-received/repayment/${loanId}`)}
                            className="flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border border-current rounded-xl transition-all duration-300 font-medium whitespace-nowrap"
                          >
                            <Plus className="w-4 h-4" /> Add Repayment
                          </button>
                        )}
                      </div>

                      {!loan.repayments || loan.repayments.length === 0 ? (
                        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                          <ReceiptText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 font-medium">No repayments recorded yet</p>
                          {(loan.status === 'approved' || loan.status === 'active') && (
                            <button
                              onClick={() => navigate(`/loans-received/repayment/${loanId}`)}
                              className="mt-3 text-sm text-buttonBg font-semibold hover:underline"
                            >
                              Record first repayment →
                            </button>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b-2 border-gray-200">
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Payment ID</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Amount</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Payment Mode</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Transaction ID</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Paid By</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {loan.repayments.map((repayment) => (
                                  <tr key={repayment.repayment_id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm font-medium">{repayment.repayment_id}</td>
                                    <td className="px-4 py-3 text-sm">{formatDate(repayment.payment_date)}</td>
                                    <td className="px-4 py-3 text-sm font-bold text-emerald-600">{formatCurrency(repayment.amount)}</td>
                                    <td className="px-4 py-3 text-sm capitalize">{repayment.payment_mode.replace(/_/g, ' ')}</td>
                                    <td className="px-4 py-3 text-sm">{repayment.transaction_id || 'N/A'}</td>
                                    <td className="px-4 py-3 text-sm">{repayment.paid_by}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div className="lg:hidden space-y-3">
                            {loan.repayments.map((repayment) => (
                              <div key={repayment.repayment_id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                <div className="flex justify-between items-start gap-2 mb-3">
                                  <div className="min-w-0">
                                    <p className="font-semibold text-emerald-600">{formatCurrency(repayment.amount)}</p>
                                    <p className="text-xs text-gray-600">{repayment.repayment_id}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">{formatDate(repayment.payment_date)}</p>
                                  </div>
                                  <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded capitalize whitespace-nowrap">
                                    {repayment.payment_mode.replace(/_/g, ' ')}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                                  <div>
                                    <span className="text-gray-500">Transaction ID:</span>
                                    <p className="font-medium break-all">{repayment.transaction_id || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Paid By:</span>
                                    <p className="font-medium">{repayment.paid_by}</p>
                                  </div>
                                  {repayment.remarks && (
                                    <div className="col-span-2">
                                      <span className="text-gray-500">Remarks:</span>
                                      <p className="font-medium">{repayment.remarks}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                </div>
              </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4">
              <XCircle className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-bold text-gray-900">Reject Loan</h3>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason <span className="text-red-500">*</span></label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="danger" onClick={handleReject} className="flex-1">
                Reject Loan
              </Button>
              <Button variant="secondary" onClick={() => { setShowRejectModal(false); setRejectionReason(''); }} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ViewLoanReceived;